import React, { useMemo, useState, useEffect, useRef, useReducer } from "react";
import { getLocale, useIntl, useSelector } from "umi";
import moment from "moment";

import PerformanceMetricsTable, { SortBy } from "@/pages/dashboard/components/PerformanceMetricsTable";
import SelectShowValueType, { ShowValueType } from "@/pages/dashboard/pages/overview/components/OverviewTable/components/SelectShowValueType";
import MetricsSettings, { defaultStatisticsTypeValue } from "@/pages/dashboard/components/MetricsSettings";

import {
  getTableRequestParams, useStatisticsDataFormat,
  useTableColumns,
  useTableDataFormat,
  useTableState
} from "@/pages/dashboard/pages/overview/components/OverviewTable/hooks";
import useTableLoclesIntl from "@/pages/dashboard/pages/overview/_config/useTableLoclesIntl";
import { LANGUAGE_MAP } from "@/pages/dashboard/_config/constant";
import "@/pages/dashboard/assets/scss/page/Overlay/components/tableConext.scss";
import {
  downLoadDayPerformanceTable,
  downLoadDayPerformanceTableXh,
  downLoadMonthlyPerformanceTable,
  downLoadPcConsoleDayPerformanceTable,
  downLoadWeeklyPerformanceTable,
  getDayPerformanceTable,
  getDayPerformanceTableXh,
  getMonthlyPerformanceTable,
  getPcConsoleDayPerformanceTable,
  getWeeklyPerformanceTable,
} from '@/pages/dashboard/services/overview';
import { dailyRequest } from "../../_config/dailyRequest";
import DimensionAccumulateRow from "@/pages/dashboard/components/DimensionAccumulateRow";
import {useAccumulateOptionsByFilterValue} from "@/pages/dashboard/components/DimensionAccumulate/hooks";
import { overviewMonthlyRequest } from "../../_config/overviewMonthlyRequest";
import { useSelectArrObj } from "@/pages/dashboard/utils/customHook";
import { formatSingleTime } from "@/pages/dashboard/utils/utils";
import AIAnalysisButton from "@/pages/dashboard/components/AIAnalysisButton";
import { isRateSort } from "@/pages/dashboard/components/PerformanceMetricsTable/components/_utils";
import { useOverLimitTrigger } from "@/pages/dashboard/components/OverLimitTip";

export default function Table({ 
  filterValue, 
  filterLoading, 
  gameCode, 
  explainInfo, 
  tableType, 
  gameConfig,
  dailyReqType='normal' ,
  logObj
}) {
  // 表格 state 状态
  const {
    loading, setLoading,
    isError, setIsError,
    paging, setPaging,
    sort, setSort,
    total, setTotal,
    tableData, setTableData,
    statisticsData, setStatisticsData,
  } = useTableState();

  const {formatMessage} = useIntl();
  const filterValueSort = useRef(filterValue);

  useEffect(()=>{
    filterValueSort.current = {filterValue,paging};
  },[filterValue,paging]);

  const [columns, setColumns] = useState([]);
  const [frontLogicalFlag, setFrontLogicalFlag] = useState<string | undefined>(undefined);
  const { overLimitBarTriggerKey, handleResponse } = useOverLimitTrigger();
  // 列定义, 中英文切换(全部), Accumulate关闭(仅仅维度列),
  const { COLUMN_CONFIG_ALL } = useTableLoclesIntl();
  const COLUMN_CONFIG = COLUMN_CONFIG_ALL[tableType] || [];

  const {
    DATE_COLUMN,
    getAccumulateColumns,
    getMetricColumns
  } = useTableColumns();
  const [showMetrics, setShowMetrics] = useState(Object.keys(COLUMN_CONFIG));
  const [selectType, setSelectType] = useState<ShowValueType>("value_only");
  const [showStatistic, setShowStatistic] = useState(defaultStatisticsTypeValue);
  const [isCallingRateSortApi, setIsCallingRateSortApi] = useState<boolean>(false);

  // let options = useAccumulateOptionsByFilterValue(filterValue,true);
  const [accumulated, setAccumulated] = useState<string[]>(['date']);
  const accumulateOldValueRef = useRef(['date']);

  let SEPARATED_OPTIONS = useSelectArrObj(filterValue?.filterValue);
  SEPARATED_OPTIONS = SEPARATED_OPTIONS?.filter(i => i?.groupKey === 'separated');
  SEPARATED_OPTIONS.unshift({
    groupKey: 'separated',
    value: 'date',
    text: formatMessage({ id: 'common_by_Date' }),
    disabled: true,
  });

  // 不同日期维度下对应的请求方法
  const GET_DATA_METHOD: any = {
    "day": {
      getPerformanceTable: dailyRequest.getDayPerformanceTable?.[dailyReqType],
      downLoadPerformanceTable: dailyRequest.downLoadDayPerformanceTable?.[dailyReqType],
    },
    "week": {
      getPerformanceTable: getWeeklyPerformanceTable,
      downLoadPerformanceTable: downLoadWeeklyPerformanceTable,
    },
    "month": {
      getPerformanceTable: overviewMonthlyRequest.getMonthlyPerformanceTable[dailyReqType],
      downLoadPerformanceTable: overviewMonthlyRequest.downLoadMonthlyPerformanceTable[dailyReqType],
    },
    "pcConsoleDay": {
      getPerformanceTable: getPcConsoleDayPerformanceTable,
      downLoadPerformanceTable: downLoadPcConsoleDayPerformanceTable,
    }
  };

  useEffect(() => {
    if (filterLoading) return;
    setPaging({ pageIndex: 1, pageSize: paging.pageSize });
    setSort([]);
    const params = getRequestParams({ pageIndex: 1, pageSize: paging.pageSize }, [],filterValueSort?.current?.filterValue);
    getTableData(params);

  }, [filterValue, filterLoading]);

  useEffect(() => {
    const dateColumn = filterValue.timeType === "Month" ? { ...DATE_COLUMN, width: 136 } : DATE_COLUMN;
    const columns = [
      dateColumn,
      ...getAccumulateColumns(accumulateOldValueRef?.current),
      ...getMetricColumns(showMetrics, COLUMN_CONFIG, explainInfo, selectType)
    ];
    setColumns(columns);
  }, [accumulateOldValueRef?.current, selectType, filterValue.timeType, showMetrics, getLocale(), explainInfo]);

  // 表格请求方法
  const { getPerformanceTable, downLoadPerformanceTable } = GET_DATA_METHOD[tableType];
  const getTableData = (params) => {
    setIsError(false);
    setLoading(true);
    getPerformanceTable(params, LANGUAGE_MAP[getLocale()]).then((resp) => {
      setLoading(false);
      setTableData(resp.metrics_list || []);
      setStatisticsData(resp.aggregation || []);
      // 设置总条数
      setTotal(resp.row_count);
      setFrontLogicalFlag(resp?.front_logical_flag);
      handleResponse(resp?.front_logical_flag);
    }).catch(e => {
      setLoading(false);
      setIsError(true);
      console.log("overview 宽表请求错误: ", filterValue.timeType, e);
    });
  };

  // 获取表格上传参数
  const getRequestParams = (paging, sort,filterValue, otherParams?) => {
    // overview页面的日期上传参数逻辑与其他页面不同, 故特殊处理
    const { filterValue: filterVal, newFilter, diff } = filterValue;
    let startTime = filterVal?.rangeTime?.[0];
    const endTime = filterVal?.rangeTime?.[1];
    const periodObj = tableType === 'pcConsoleDay' ? {
      period: 'daily'
    } : {};

    /**
     * day 少于七天补足七天
     * week 少于六周补足六周
     * month 少于六月补足六月
     */
    if (newFilter && diff) {
      switch (filterValue.timeType) {
        case "Day": {
          startTime = moment(startTime).subtract(7, 'day').format('YYYY-MM-DD');
          break;
        }
        case "Week": {
          startTime = moment(endTime).subtract(5, 'week').format('YYYY-MM-DD');
          break;
        }
        case "Month": {
          startTime = moment(startTime).subtract(5, 'month').format('YYYY-MM-DD');
          break;
        }
      }
    }


    return getTableRequestParams({
      gameCode, filterValue,
      accumulated: accumulateOldValueRef?.current,
      sort,
      paging,
      selectType,
      start_date: moment(startTime).format("YYYYMMDD"),
      end_date: moment(endTime).format("YYYYMMDD"),
      // start_date: "20211122",
      // end_date: "20220217",
      ...otherParams,
      ...periodObj
    });
  };
  // 表格数据
  const records = [
    ...useTableDataFormat(tableData, COLUMN_CONFIG, filterValue.timeType),
    ...useStatisticsDataFormat(statisticsData, COLUMN_CONFIG, showStatistic)
  ];

  const copyData = [
    ...useTableDataFormat(tableData, COLUMN_CONFIG, filterValue.timeType, true),
    ...useStatisticsDataFormat(statisticsData, COLUMN_CONFIG, showStatistic,true)
  ];

  // 下载弹窗指标options
  const metricOptions = useMemo(() => Object.keys(COLUMN_CONFIG).map((key) => ({ value: key, text: COLUMN_CONFIG[key]?.name })), [COLUMN_CONFIG_ALL]);
  
  const onSortChange=  (sorts) => {
    // 排序
    setSort(sorts);
    // 发起请求
    const params = getRequestParams(filterValueSort?.current?.paging, sorts,filterValueSort?.current?.filterValue);
    // forceUpdate()
    getTableData(params);
    setTimeout(() => {
      setIsCallingRateSortApi(false);
    }, 0);
  };

  const onSortChangeRef = useRef<(sorts?: SortBy[]) => void>(() => {});
  onSortChangeRef.current = onSortChange;

  return (
    <PerformanceMetricsTable
      title={formatMessage({ id: "overview_Overall_Performance" })}
      // className='overall-conext'
      columns={columns}
      records={records}
      copyConfig={
        {
          copyData
        }
      }
      selectType={selectType}
      timeType={filterValue?.timeType}
      sorting={{
        defaultSorts: sort,
        onSortChange,

      }}
      paging={{
        pageIndex: paging.pageIndex,
        pageSize: paging.pageSize,
        recordCount: total,
        pageSizeOptions: [10, 20, 30, 40, 50, 100, 200],
        onPagingChange(query) {
          // 切换分页
          setPaging(query);
          // 发起请求
          const params = getRequestParams(query, sort,filterValueSort?.current?.filterValue);
          getTableData(params);
        }
      }}
      topTip={{
        left: (<div className={`overall-conext-margin js-overview-table-dimension`}>
          <DimensionAccumulateRow 
            options={SEPARATED_OPTIONS}
            value={accumulated}
            onChange={(value)=>{
              setAccumulated(value);
              if (accumulateOldValueRef?.current?.toString() !== value?.toString()) {
                accumulateOldValueRef.current = value;
                // 重置分页和排序 (不把重置过程写到 getRequestParams中, 是想要保证方法的独立性, 不要有多种作用混杂在一起)
                setPaging({ pageIndex: 1, pageSize: paging.pageSize });
                setSort([]);
                // 发起请求
                const params = getRequestParams({ pageIndex: 1, pageSize: paging.pageSize }, [],filterValueSort?.current?.filterValue);
                getTableData(params);
              }
            }}
          />
          <SelectShowValueType
            timeType={filterValue.timeType}
            value={selectType}
            onChange={(value) => {
              setSelectType(value as ShowValueType);
              if (isRateSort(sort)) {
                setIsCallingRateSortApi(true);
                setTimeout(() => {
                  if (sort?.[0].by.includes(value))
                    onSortChangeRef.current(value === 'value_only' ? [] : sort);
                }, 0);
              }
            }}
            isShowDod
            isShowSdlw
          />
        </div>),
        right: (<>
          <AIAnalysisButton componentKey="OverviewTable" pageId={logObj?.pageId} agentId={gameConfig?.aiAnalysisAgentId}/>
          <MetricsSettings
            onConfirm={({ metricValues, statisticsType }) => {
              setShowStatistic(statisticsType);
              setShowMetrics(metricValues);
            }}
            disabledOptions={[{ value: "date", text: formatMessage({ id: "common_date" }) }]}
            metricOptions={metricOptions}
          />
        </>)
      }}
      onResizeEnd={(columns) => setColumns(columns)}
      isShowProgressBar
      loading={loading || isCallingRateSortApi}
      isError={isError}
      downloadConfig={{
        defaultValue: showMetrics,
        onDownload: ({ isDownloadStatistics, metrics, dataType, fileFormat }) => {
          const params = getRequestParams(paging, sort,filterValueSort?.current?.filterValue, {
            metrics,
            show_dod: dataType === "show_dod",
            show_sdlw: dataType === "show_sdlw",
            file_format: fileFormat,
            aggregates: isDownloadStatistics ? showStatistic : []
          });
          return downLoadPerformanceTable(params, LANGUAGE_MAP[getLocale()]);
        },
        timeType: filterValue.timeType,
        metricOptions,
        showData: { valueOnly: true, showDod: true, showSdlw: true }
      }}
      frontLogicalFlag={frontLogicalFlag}
      limitCount={gameConfig?.tableConfig?.overLimitCount}
      barLimitCount={gameConfig?.tableConfig?.overLimitBarCount}
      barDownloadDisabled={gameConfig?.tableConfig?.barDownloadDisabled}
      overLimitBarTriggerKey={overLimitBarTriggerKey}
    />);
}
